import { call, put, race, take, takeLatest } from 'redux-saga/effects';
import { MODAL_TYPES } from 'src/appConfig/modal';
import { PATHS } from 'src/appConfig/paths';
import { TabIdx } from 'src/containers/HomeContainers/Timesheet/tableHelpers';
import { ErrorService, Navigator, TokenService } from 'src/services';
import { Apis } from 'src/services/api';
import { getInitialDateRange } from 'src/utils/momentUtils';
import { getType } from 'typesafe-actions';
import { callApi } from '../commonSagas/callApi';
import modalActions from '../modal/actions';
import { setActiveTimesheetTab, setDateRange } from '../timesheet/actions';
import {
  clearLocalApiToken,
  confirmSignUpAsync,
  exchangeTokenAsync,
  forgotPasswordAsync,
  getAuthorizedUserAsync,
  getPermissionAsync,
  resendSignUpAsync,
  setAuthorizedUser,
  // setGuest,
  signInAsync,
  signOutAsync,
  signUpAsync,
  submitForgotPasswordAsync,
} from './actions';
import { AuthPermission, AuthUser } from './types';

function* signIn(api, action: ReturnType<typeof signInAsync.request>) {
  try {
    yield call(api, action.payload);
    yield put(signInAsync.success());
  } catch (err) {
    yield put(signInAsync.failure(err));

    if ((err as AuthError).code === 'UserNotConfirmedException') {
      yield put(resendSignUpAsync.request({ username: action.payload.username }));
      yield call(showConfirmEmailModal, action.payload.username);

      // wait for confirming email success to re-signin
      yield take(getType(confirmSignUpAsync.success));
      yield put(signInAsync.request(action.payload));
    }
  }
}

function* resendSignUp(api, action: ReturnType<typeof resendSignUpAsync.request>) {
  yield call(callApi, api, { asyncAction: resendSignUpAsync }, action.payload);
}

function* confirmSignUp(api, action: ReturnType<typeof confirmSignUpAsync.request>) {
  yield call(callApi, api, { asyncAction: confirmSignUpAsync }, action.payload);
}

function* signUp(api, action: ReturnType<typeof signUpAsync.request>) {
  yield call(callApi, api, { asyncAction: signUpAsync, responseExtractor: () => action.payload.email }, action.payload);

  // wait for confirmation is successful to do first log in
  // cancel this sign-in request in case user didn't confirm email but try signing-in
  const { confirmedEmail } = yield race({
    signInRequested: yield take(getType(signInAsync.request)),
    confirmedEmail: yield take(getType(confirmSignUpAsync.success)),
  });

  if (confirmedEmail) {
    yield put(signInAsync.request({ username: action.payload.email, password: action.payload.password }));
  }
}

function* signOut(api) {
  yield put(clearLocalApiToken());
  yield call(callApi, api, {
    asyncAction: signOutAsync,
  });
}

function* forgotPassword(api, action) {
  try {
    yield call(api, action.payload);
    yield put(forgotPasswordAsync.success());
    Navigator.navigate(PATHS.resetPassword, {
      email: action.payload.email,
      isFirstTimeLogin: action.payload.isFirstTimeLogin ?? false,
    });
  } catch (err) {
    yield put(forgotPasswordAsync.failure(err));
    ErrorService.handler(err);
  }
}

function* submitForgotPassword(api, action) {
  yield call(callApi, api, { asyncAction: submitForgotPasswordAsync }, action.payload);
}

function* getPermission(api, action: ReturnType<typeof getPermissionAsync.request>) {
  try {
    const permission = yield call(api);
    yield put(getPermissionAsync.success((permission.data as AuthPermission[]).map(p => p.name)));
  } catch (err) {
    yield put(getPermissionAsync.failure(err));
  }
}

function* showConfirmEmailModal(username: string) {
  yield put(
    modalActions.showModal({
      type: MODAL_TYPES.emailConfirmationModal,
      data: {
        value: username,
      },
    }),
  );
}

function* handleSignUpSuccess({ payload: username }: ReturnType<typeof signUpAsync.success>) {
  yield call(showConfirmEmailModal, username);
}

function* closeConfirmEmailModal() {
  yield put(modalActions.closeModal());
}

function* exchangeToken(api, action: ReturnType<typeof exchangeTokenAsync.request>) {
  TokenService.clearToken();
  yield call(
    callApi,
    api,
    { asyncAction: exchangeTokenAsync, responseExtractor: response => ({ token: response.data.token }) },
    {},
  );
}

function exchangeTokenSuccess(action: ReturnType<typeof exchangeTokenAsync.success>) {
  TokenService.setToken(action.payload.token);
}

function* getAuthorizedUser(api, action: ReturnType<typeof getAuthorizedUserAsync.request>) {
  try {
    const response = yield call(api);
    const user = response?.data ?? (null as AuthUser);
    if (user.isActive) {
      yield put(getAuthorizedUserAsync.success());
      yield put(
        setAuthorizedUser({
          isAuthenticated: true,
          authUser: user,
        }),
      );
      const isAdmin = user?.role === 'ADMIN';
      yield put(setActiveTimesheetTab(isAdmin ? TabIdx.All : TabIdx.Week));
      yield put(setDateRange(getInitialDateRange(isAdmin)));
    } else {
      yield put(getAuthorizedUserAsync.failure({ name: 'ERROR_USER_INACTIVE', message: 'Your account is inactive.' }));
    }
  } catch (err) {
    yield put(getAuthorizedUserAsync.failure(err));
    yield put(
      setAuthorizedUser({
        isAuthenticated: false,
        authUser: null,
      }),
    );
  }
}

export default function authSaga(apiInstance: Apis) {
  return [
    takeLatest(signUpAsync.request, signUp, apiInstance.signUp),
    takeLatest(signUpAsync.success, handleSignUpSuccess),
    takeLatest(signInAsync.request, signIn, apiInstance.signIn),
    takeLatest(getPermissionAsync.request, getPermission, apiInstance.getPermission),
    takeLatest([signOutAsync.request, getAuthorizedUserAsync.failure], signOut, apiInstance.signOut),
    takeLatest(resendSignUpAsync.request, resendSignUp, apiInstance.resendSignUp),
    takeLatest(confirmSignUpAsync.request, confirmSignUp, apiInstance.confirmSignUp),
    takeLatest([confirmSignUpAsync.success, resendSignUpAsync.failure], closeConfirmEmailModal),
    takeLatest(forgotPasswordAsync.request, forgotPassword, apiInstance.forgotPassword),
    takeLatest(submitForgotPasswordAsync.request, submitForgotPassword, apiInstance.submitForgotPassword),
    takeLatest(exchangeTokenAsync.request, exchangeToken, apiInstance.exchangeToken),
    takeLatest(exchangeTokenAsync.success, exchangeTokenSuccess),
    takeLatest(
      [getAuthorizedUserAsync.request, exchangeTokenAsync.success],
      getAuthorizedUser,
      apiInstance.getAuthorizedUser,
    ),
  ];
}
